import { fromUnixTime, getUnixTime, formatDistanceToNow } from "date-fns";
import { get } from "lodash";
import mediator from "@tvg/mediator";
import { VideoCardProps } from "@fdr/atomic-ui/_molecules/VideoCard";
import {
  JWPlayerAPIResponse,
  JWPlayerProgramPageResponse,
  VideoNativeRotation
} from "./types";

export const formatTimeAgo = (date: Date) => {
  const rtf = new Intl.RelativeTimeFormat("en", {
    numeric: "always"
  });

  const dateInMs = Number(String(getUnixTime(date)).padEnd(13, "0"));
  const timeDifferenceMs = dateInMs - new Date().getTime();
  const unitMsConversions = {
    second: 1000,
    minute: 60000,
    hour: 3600000,
    day: 86400000
  };

  const determineUnitType = (timeDiffMs: number) => {
    if (
      Math.abs(timeDiffMs) > unitMsConversions.day ||
      Math.abs(timeDiffMs) > unitMsConversions.hour * 23.5
    ) {
      return "day";
    }

    if (
      Math.abs(timeDiffMs) > unitMsConversions.hour ||
      Math.abs(timeDiffMs) > unitMsConversions.minute * 59.5
    ) {
      return "hour";
    }

    if (
      Math.abs(timeDiffMs) > unitMsConversions.minute ||
      Math.abs(timeDiffMs) > unitMsConversions.second * 59.5
    ) {
      return "minute";
    }

    return "second";
  };

  if (determineUnitType(timeDifferenceMs) === "second") return "just added";
  if (
    determineUnitType(timeDifferenceMs) === "day" &&
    Math.abs(
      timeDifferenceMs / unitMsConversions[determineUnitType(timeDifferenceMs)]
    ) <= 30
  ) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  if (
    determineUnitType(timeDifferenceMs) === "day" &&
    Math.abs(
      timeDifferenceMs / unitMsConversions[determineUnitType(timeDifferenceMs)]
    ) > 30
  ) {
    return "over a month ago";
  }

  return rtf.format(
    Math.round(
      timeDifferenceMs / unitMsConversions[determineUnitType(timeDifferenceMs)]
    ),
    determineUnitType(timeDifferenceMs)
  );
};

export const getPlayerDimensions = ({
  height,
  width
}: {
  height: number;
  width: number;
}) => {
  const isPortraitVideo = height > width;
  const defaultWidth = 390;

  if (isPortraitVideo) {
    return { height: 720, width: defaultWidth };
  }

  return { height: 220, width: defaultWidth };
};

export const getJWPlaylistVideos = async (
  playlistUrl: string,
  props: { playerScript: string; licenseKey: string },
  outputCompleteObject?: boolean
): Promise<VideoCardProps[] | JWPlayerProgramPageResponse> =>
  fetch(playlistUrl)
    .then((res) => res.json())
    .then((data: JWPlayerAPIResponse) => {
      const mappedPlaylist: VideoCardProps[] = data.playlist.map((media) => {
        const uploadedAt = formatTimeAgo(
          fromUnixTime(get(media, "pubdate", ""))
        );
        const [highestQualitySource] = media.sources
          .filter((source) => source.type === "video/mp4")
          .sort((a, b) => b.height * b.width - a.height * a.width);

        return {
          description: {
            title: get(media, "title", ""),
            description: get(media, "description", ""),
            category: get(media, "category"),
            uploadedAt
          },
          video: {
            playerId: get(media, "mediaid"),
            duration: get(media, "duration", 0),
            src: highestQualitySource?.file ?? "",
            ...getPlayerDimensions({
              width: highestQualitySource?.width,
              height: highestQualitySource?.height
            }),
            thumbnail: get(media, "image"),
            playerScript: props.playerScript,
            licenseKey: props.licenseKey,
            tracks: get(media, "tracks", [])
          }
        };
      });

      const completeObject: JWPlayerProgramPageResponse = {
        ...data,
        playlist: mappedPlaylist
      };

      return outputCompleteObject ? completeObject : mappedPlaylist;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
      return [];
    });

export const toggleNativeRotation = (locationScreen: VideoNativeRotation) => {
  mediator.ios.dispatch({
    type: "IPHONE_LOCK_SCREEN_ROTATION",
    payload: { lockScreenRotation: locationScreen }
  });
};
